import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdAccessTime, MdLocationOn, MdDirectionsCar, MdArrowForward, MdDirectionsCarFilled } from 'react-icons/md';
import { getIncompleteBooking, clearIncompleteBooking } from '../utils/bookingUtils';
import { updateCarURL } from '../utils/urlParams';

const IncompleteBookingBanner = () => {
  const [incompleteBooking, setIncompleteBooking] = useState(null);
  const [imageError, setImageError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const booking = getIncompleteBooking();
    if (booking) {
      setIncompleteBooking(booking);
    }
  }, []);

  const handleContinueBooking = () => {
    if (incompleteBooking) {
      try {
        // Create carInfo object following the same pattern as CarDetails handleReserve
        const carInfo = {
          vendor: incompleteBooking.selectedCar?.partner?.code || incompleteBooking.vendor || '',
          carClass: incompleteBooking.selectedCar?.code || incompleteBooking.carClass || '',
          pickupLocation: incompleteBooking.pickupLocation || '',
          dropoffLocation: incompleteBooking.dropoffLocation || '',
          pickupDate: incompleteBooking.pickupDate || '',
          dropoffDate: incompleteBooking.dropoffDate || '',
          rateCode: incompleteBooking.selectedCar?.rateCode || incompleteBooking.rateCode || '',
        };

        console.log("CarInfo being passed to checkout from incomplete booking:", carInfo);
        
        // Validate that we have the minimum required data
        if (!carInfo.vendor || !carInfo.carClass || !carInfo.pickupLocation || !carInfo.dropoffLocation) {
          console.error("Missing required car information for checkout");
          alert("Missing required booking information. Please start a new booking.");
          handleDismiss();
          return;
        }
        
        // Use the same navigation pattern as Reserve button - open in new tab
        updateCarURL(navigate, carInfo, true);
      } catch (error) {
        console.error("Error continuing booking:", error);
        alert("Error continuing booking. Please try again or start a new booking.");
      }
    }
  };

  const handleDismiss = () => {
    setIncompleteBooking(null);
    clearIncompleteBooking();
  };

  if (!incompleteBooking) {
    return null;
  }

  const formatDate = (dateStr) => {
    try {
      if (!dateStr) return 'N/A';
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    } catch {
      return 'N/A';
    }
  };

  const formatTime = (timeStr) => {
    try {
      if (!timeStr) return 'N/A';
      
      // Handle different time formats
      let time = timeStr;
      
      // If it's a full datetime string, extract just the time
      if (timeStr.includes('T')) {
        time = timeStr.split('T')[1];
      }
      
      // Remove milliseconds if present
      if (time.includes('.')) {
        time = time.split('.')[0];
      }
      
      // Remove timezone if present
      if (time.includes('+') || time.includes('-')) {
        time = time.split(/[+-]/)[0];
      }
      
      const [hours, minutes] = time.split(':');
      if (!hours || !minutes) {
        return 'N/A';
      }
      
      // Validate hours and minutes
      const hour = parseInt(hours);
      const minute = parseInt(minutes);
      if (isNaN(hour) || isNaN(minute) || hour < 0 || hour > 23 || minute < 0 || minute > 59) {
        return 'N/A';
      }
      
      const date = new Date(2000, 0, 1, hour, minute);
      if (isNaN(date.getTime())) {
        return 'N/A';
      }
      
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    } catch (error) {
      console.error("Error formatting time:", error);
      return 'N/A';
    }
  };

  // Get car image URL with fallback
  const getCarImageUrl = () => {
    if (incompleteBooking.selectedCar?.imageUrl && !imageError) {
      return incompleteBooking.selectedCar.imageUrl;
    }
    // Fallback to a default car image
    return '/default-car.svg';
  };

  // Get car display name
  const getCarDisplayName = () => {
    if (incompleteBooking.selectedCar?.exampleDisplay) {
      return incompleteBooking.selectedCar.exampleDisplay;
    }
    if (incompleteBooking.selectedCar?.child_Filter) {
      return incompleteBooking.selectedCar.child_Filter;
    }
    if (incompleteBooking.selectedCar?.nameDisplay) {
      return incompleteBooking.selectedCar.nameDisplay;
    }
    return 'Selected Vehicle';
  };

  // Handle image load error
  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <div className="bg-white border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow duration-200 mt-4">
      <div className="w-full">
        {/* Desktop Layout */}
        <div className="hidden md:flex items-center justify-between">
          {/* Left Section - Clock & Car Image & Content */}
          <div className="flex items-center gap-4">
            {/* Status Icon */}
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <MdAccessTime className="text-blue-600" size={18} />
              </div>
            </div>

            {/* Car Image - Moved to right of clock */}
            <div className="flex-shrink-0">
              {imageError ? (
                <div className="w-16 h-12 bg-gray-50 rounded-lg border border-gray-200 flex items-center justify-center">
                  <MdDirectionsCarFilled className="text-gray-400" size={20} />
                </div>
              ) : (
                <img 
                  src={getCarImageUrl()} 
                  alt={getCarDisplayName()}
                  className="w-16 h-12 object-contain rounded-lg border border-gray-200 bg-gray-50"
                  onError={handleImageError}
                />
              )}
            </div>
            
            {/* Content */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-base font-semibold text-gray-900">
                  <>
                    {incompleteBooking.selectedCar?.nameDisplay || incompleteBooking.selectedCar?.child_Filter || incompleteBooking.selectedCar?.exampleDisplay || 'Selected Vehicle'} <span className="text-xs font-xs">or similar</span>
                  </>
                </h3>
                <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                  Incomplete
                </span>
              </div>
              
              <div className="flex items-center gap-4 text-sm text-gray-600">
                {/* Location */}
                <div className="flex items-center gap-1">
                  <MdLocationOn size={14} className="text-blue-500" />
                  <span>
                    {incompleteBooking.pickupLocationName +" ("+incompleteBooking.pickupLocation+")" || 'N/A'} → {incompleteBooking.dropoffLocationName +" ("+incompleteBooking.dropoffLocation+")" || 'N/A'}
                  </span>
                </div>
                
                {/* Dates */}
                <div className="flex items-center gap-1">
                  <MdDirectionsCar size={14} className="text-green-500" />
                  <span>
                    {formatDate(incompleteBooking.pickupDate)} at {formatTime(incompleteBooking.pickupDate)} - {formatDate(incompleteBooking.dropoffDate)} at {formatTime(incompleteBooking.dropoffDate)}
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Right Section - Actions */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleDismiss}
              className="text-gray-500 hover:text-gray-700 text-sm font-medium transition-colors"
            >
              Dismiss
            </button>
            <button
              onClick={handleContinueBooking}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200"
            >
              Continue Booking
              <MdArrowForward size={16} />
            </button>
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="md:hidden">
          {/* Top Row - Icons, Title & Actions */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              {/* Status Icon */}
              <div className="flex-shrink-0">
                <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center">
                  <MdAccessTime className="text-blue-600" size={14} />
                </div>
              </div>

              {/* Car Image */}
              <div className="flex-shrink-0">
                {imageError ? (
                  <div className="w-12 h-8 bg-gray-50 rounded border border-gray-200 flex items-center justify-center">
                    <MdDirectionsCarFilled className="text-gray-400" size={14} />
                  </div>
                ) : (
                  <img 
                    src={getCarImageUrl()} 
                    alt={getCarDisplayName()}
                    className="w-12 h-8 object-contain rounded border border-gray-200 bg-gray-50"
                    onError={handleImageError}
                  />
                )}
              </div>
              
              {/* Title & Badge */}
              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-gray-900 leading-tight">
                  Continue Booking
                </h3>
                <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded-full self-start">
                  Incomplete
                </span>
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleDismiss}
                className="text-gray-500 hover:text-gray-700 text-xs font-medium transition-colors"
              >
                Dismiss
              </button>
              <button
                onClick={handleContinueBooking}
                className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-xs font-medium transition-colors duration-200"
              >
                Continue
                <MdArrowForward size={12} />
              </button>
            </div>
          </div>
          
          {/* Bottom Row - Details */}
          <div className="space-y-1 pl-9">
            {/* Location */}
            <div className="flex items-center gap-1">
              <MdLocationOn size={12} className="text-blue-500 flex-shrink-0" />
              <span className="text-xs text-gray-600 truncate">
                {incompleteBooking.pickupLocationName || incompleteBooking.pickupLocation || 'N/A'} → {incompleteBooking.dropoffLocationName || incompleteBooking.dropoffLocation || 'N/A'}
              </span>
            </div>
            
            {/* Dates */}
            <div className="flex items-center gap-1">
              <MdDirectionsCar size={12} className="text-green-500 flex-shrink-0" />
              <span className="text-xs text-gray-600">
                {formatDate(incompleteBooking.pickupDate)} at {formatTime(incompleteBooking.pickupDate)} - {formatDate(incompleteBooking.dropoffDate)} at {formatTime(incompleteBooking.dropoffDate)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncompleteBookingBanner;
